import type { FC } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import {
  ThemeProvider,
  StyledEngineProvider,
  CssBaseline,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import GlobalStyles from 'src/components/GlobalStyles';
import ScrollReset from 'src/components/ScrollReset';
import GoogleAndMetaAnalytics from 'src/components/GoogleAndMetaAnalytics';
import { AuthProvider } from 'src/contexts/FirebaseAuthContext';
import useSettings from 'src/hooks/useSettings';
import { createTheme } from 'src/theme';
import routes, { renderRoutes } from 'src/routes';
import ErrorBoundary from 'src/components/ErrorBoundary';
import { UserContextProvider } from './contexts/UserContext';
import { captureMetaAttribution } from 'src/utils/metaAttribution';

// Capture fbclid, _fbp, and UTM params on first load
captureMetaAttribution();

const App: FC = () => {
  const { settings } = useSettings();

  const theme = createTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme,
  });

  return (
    <ErrorBoundary>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <SnackbarProvider maxSnack={3}>
              <BrowserRouter>
                <GoogleAndMetaAnalytics />
                <AuthProvider>
                  <UserContextProvider>
                    <CssBaseline />
                    <GlobalStyles />
                    <ScrollReset />
                    {renderRoutes(routes)}
                  </UserContextProvider>
                </AuthProvider>
              </BrowserRouter>
            </SnackbarProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ErrorBoundary>
  );
};

export default App;
